<template>
  <tr class="ce-productSheet">
    <td class="ce-productSheet-actions">
      <div class="flex items-center">
        <span class="ce-productSheet-typeIconWrapper">
          <!-- TODO proper icon depending on the document type (if we have anything besides pdf) -->
          <fa-icon
            @click="downloadAsset(asset)"
            v-if="!downloadRunning"
            :icon="['far', 'file-pdf']" />

          <fa-icon
            v-else
            icon="spinner"
            spin />
        </span>

        <a
          class="ce-productSheet-favoriteCta"
          href=""
          @click.prevent="toggleFavorite">
          <fa-icon :icon="favoriteStateIcon" />
        </a>

        <copy-to-clipboard
          v-if="showDirectDownloadLinkCta"
          :content="directDownloadLink">
          <a
            class="ce-productSheet-getLinkCta"
            href="">
            <fa-icon icon="link" />
          </a>
        </copy-to-clipboard>

        <a
          v-if="showHistoryAction"
          href=""
          @click.prevent="showHistory">
          <fa-icon icon="history" />
        </a>
      </div>
    </td>

    <td
      class="ce-productSheet-label"
      @click="downloadAsset(asset)">
      {{ label }}

      <fa-icon
        v-if="mostRestrictiveAccessLevel && mostRestrictiveAccessLevel.code === 'internal'"
        transform="up-6 shrink-4"
        icon="lock" />
    </td>

    <document-version-history-modal
      v-if="showHistoryAction && historyVisible"
      @close="historyVisible = false"
      :asset="asset"
      :product="product" />
  </tr>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { saveAs } from 'file-saver';
import { axiosBlobClient } from 'utils/download';
import DocumentVersionHistoryModal from 'components/product-sheets/DocumentVersionHistoryModal.vue';
import CopyToClipboard from 'components/CopyToClipboard.vue';
import { sortArray, cloneObject } from '../../utils';

export default {
  props: {
    asset: {
      type: Object,
      required: true
    },
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      downloadRunning: false,
      historyVisible: false
    };
  },
  components: { DocumentVersionHistoryModal, CopyToClipboard },
  computed: {
    ...mapState(['documentFavorite', 'fieldManifest']),
    ...mapState('user', ['activeUser']),
    ...mapGetters('user', ['isInternal']),
    ...mapState('generic', ['services', 'industries']),
    isFavored() {
      if (this.documentFavorite) {
        // No document types defined, so the whole pigment is favored
        if (!this.documentFavorite.documentTypes) return true;

        if (Array.isArray(this.documentFavorite.documentTypes)) {
          // No document types defined, so the whole pigment is favored
          if (this.documentFavorite.documentTypes.length === 0) return true;

          // Limited to some document types, check if the current asset type is included
          return this.documentFavorite.documentTypes.indexOf(this.asset.type) > -1;
        }
      }

      return false;
    },
    favoriteStateIcon() {
      return this.isFavored ? ['fas', 'star'] : ['far', 'star'];
    },
    label() {
      return `${this.asset.label} (v${this.asset.version}, ${this.asset.locale})`;
    },
    showHistoryAction() {
      return this.isInternal && !this.asset.isRootVersion && this.asset.type !== 'svhc';
    },
    industriesForTracking() {
      if (!this.asset.industries || this.industries.length === this.asset.industries.length) {
        return 'all_industries';
      }

      return this.industries.filter((ind) => this.asset.industries.includes(ind.code)).map((ind) => ind.title).join(',');
    },
    showDirectDownloadLinkCta() {
      return this.isInternal;
    },
    directDownloadLink() {
      return `${this.services.backend.url}/util/download/document/${this.asset.id}`;
    },
    manifestForAssetType() {
      return this.fieldManifest.hasOwnProperty(this.asset.type)
        ? this.fieldManifest[this.asset.type]
        : null;
    },
    accessLevelsForAsset() {
      if (this.manifestForAssetType) {
        const accessLevels = cloneObject(this.manifestForAssetType.accessLevel);

        return accessLevels.filter((acl) => this.asset.industries.includes(acl.industry));
      }

      return null;
    },
    mostRestrictiveAccessLevel() {
      const accessLevels = cloneObject(this.accessLevelsForAsset);

      if (accessLevels) {
        sortArray(accessLevels, 'weight', 'asc');

        return accessLevels.shift();
      }

      return null;
    }
  },
  methods: {
    ...mapActions(['saveDocumentFavorite', 'deleteDocumentFavorite']),
    async downloadAsset() {
      this.downloadRunning = true;

      let url = `${this.services.documentservice.url}/api/v2/document/download/${this.asset.id}`;
      if (this.activeUser) url += `?jwt=${this.activeUser.token}`;

      try {
        const response = await axiosBlobClient.get(url);
        saveAs(new Blob([response.data]), this.asset.filename);
        this.downloadRunning = false;

        this.$trackEvent(
          'Download',
          'Download Product Document',
          {
            assetType: this.asset.type,
            prd: this.product.prd,
            assetIndustries: this.industriesForTracking,
            filename: this.asset.filename
          }
        );
      } catch (e) {
        this.downloadRunning = false;
        this.$notification.error(e.response?.data?.message ?? e.message);
      }
    },
    toggleFavorite() {
      if (this.isFavored) this.removeFavorite();
      else this.addFavorite();
    },
    addFavorite() {
      const payload = {
        productId: this.product.prd,
        documentTypes: [this.asset.type]
      };

      // Merge already favored asset types
      if (this.documentFavorite && this.documentFavorite.documentTypes && Array.isArray(this.documentFavorite.documentTypes)) {
        payload.documentTypes = payload.documentTypes.concat(this.documentFavorite.documentTypes);
      }

      this.saveDocumentFavorite(payload);
    },
    removeFavorite() {
      let favoredTypes;

      if (this.documentFavorite
          && this.documentFavorite.documentTypes
          && Array.isArray(this.documentFavorite.documentTypes)
          && this.documentFavorite.documentTypes.length > 0) {
        // Is favorite for x document types, remove the current one from the array
        favoredTypes = this.documentFavorite.documentTypes.filter((type) => type !== this.asset.type);
      } else {
        // Is favorite on pigment level, get all visible asset types except the current one
        favoredTypes = this.product.assets.map((a) => a.type).filter((type) => type !== this.asset.type);
      }

      // Delete the favorite if no types are left, update otherwise
      if (favoredTypes.length === 0) {
        this.deleteDocumentFavorite(this.documentFavorite.id);
      } else {
        this.saveDocumentFavorite({
          productId: this.product.prd,
          documentTypes: favoredTypes
        });
      }
    },
    showHistory() {
      this.historyVisible = true;
    }
  }
};
</script>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-productSheet {
  cursor: pointer;

  > td {
    padding-bottom: @space-2;
  }

  span {
    text-decoration: underline;
  }

  .ce-productSheet-actions {
    > div {
      margin: 0 @space-3 0 -@space-1;

      > * {
        margin: 0 @space-1;
      }

      a {
        color: @color-text-base;
      }
    }
  }

  .ce-productSheet-label {
    transition: color 250ms ease-in-out;
    color: @color-text-base;

    a {
      color: @color-text-base;
    }

    &:hover {
      &, a {
        color: @color-light;
      }
    }
  }

  .ce-productSheet-typeIconWrapper {
    width: 16px;
  }
}
</style>
